import { cva, type VariantProps } from 'class-variance-authority';
import type { InputHTMLAttributes } from 'react';
import { cn } from '../../utils/cn';

const inputVariants = cva(
  'w-full px-base py-3 rounded-md border text-body-md bg-canvas text-ink placeholder-muted transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed',
  {
    variants: {
      state: {
        default: 'border-hairline hover:border-hairline-soft',
        error: 'border-semantic-down focus:ring-semantic-down',
        success: 'border-semantic-up focus:ring-semantic-up',
      },
    },
    defaultVariants: {
      state: 'default',
    },
  }
);

export interface InputProps
  extends InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputVariants> {
  label?: string;
  error?: string;
  success?: boolean;
  helperText?: string;
}

/**
 * Input Component
 * Coinbase-styled input with label, error and helper text support
 */
export const Input = ({
  label,
  error,
  success = false,
  helperText,
  className,
  id,
  state,
  disabled,
  ...rest
}: InputProps) => {
  const inputId = id ?? (label ? `input-${label.replace(/\s+/g, '-').toLowerCase()}` : undefined);

  // Compute state based on error/success props, or use explicit state prop
  const computedState = state || (error ? 'error' : success ? 'success' : 'default');

  return (
    <div className="space-y-1">
      {label && (
        <label htmlFor={inputId} className="block text-title-sm font-semibold text-ink">
          {label}
        </label>
      )}
      <input
        id={inputId}
        disabled={disabled}
        className={cn(
          inputVariants({
            state: computedState as 'default' | 'error' | 'success',
          }),
          className
        )}
        {...rest}
      />
      {error && <p className="text-caption text-semantic-down">{error}</p>}
      {!error && helperText && <p className="text-caption text-muted">{helperText}</p>}
    </div>
  );
};

export { inputVariants };

export default Input;
